import { Country } from '~/Api/Country';
import { ApiClientError, ApiClientErrorResponse } from '~/ApiClient';
import { ProductCompliance } from '~/Api/CzSuklCompliance';
import GeneralProduct from '~/Api/Product/Product';
import { ProductPrice } from '~/Product';
import { DataSource } from '~/Api/DataSource';
import Price from '~/utils/Price/Price';

export enum ErrorCode {
	RequiredProductSettingsMissingErrorCode = 'czech-max-price-calculation-required-product-settings-is-missing',
	IllegalPriceTypeSelectedErrorCode = 'czech-max-price-calculation-illegal-price-type-selected',
	ReferenceCountryProductMissingErrorCode = 'czech-max-price-calculation-requires-at-least-one-reference-country-product',
	OnlyOneProductPerCountryErrorCode = 'czech-max-price-calculation-requires-at-most-one-product-for-each-country',
	RequiresReferenceSettingsErrorCode = 'czech-max-price-calculation-requires-reference-settings',
	MissingThreeReferenceCountryProductsErrorCode = 'czech-max-price-calculation-requires-at-least-three-reference-country-products',
}

function mapError(error: ApiClientError): Error {
	const errorCode = error.code as ErrorCode;
	const { product, duplicateCountries } = (error.meta ?? {}) as {
		product?: { id: string };
		duplicateCountries?: { alpha2: string; name: string }[];
	};

	return new Error(
		errorCode,
		product?.id ?? null,
		duplicateCountries?.map((country) => new Country(country.alpha2, country.name)) ?? []
	);
}

export class Error {
	code: ErrorCode;
	productId: string | null;
	duplicateCountries: Country[];

	constructor(code: ErrorCode, productId: string | null, duplicateCountries: Country[]) {
		this.code = code;
		this.productId = productId;
		this.duplicateCountries = duplicateCountries;
	}
}

export class ValidationResult {
	referencePackageSizeIsMissing: boolean;
	productErrors: Error[];
	countryErrors: Error[];

	constructor(referencePackageSizeIsMissing: boolean, productErrors: Error[], countryErrors: Error[]) {
		this.referencePackageSizeIsMissing = referencePackageSizeIsMissing;
		this.productErrors = productErrors;
		this.countryErrors = countryErrors;
	}
}

export class ErrorResponse extends ApiClientErrorResponse {
	handleCalculationRequiresAtLeastOneReferenceCountryProduct(): boolean {
		return this.handle(ErrorCode.ReferenceCountryProductMissingErrorCode);
	}

	handleCalculationRequiresAtMostOneProductForEachCountry(): boolean {
		return this.handle(ErrorCode.OnlyOneProductPerCountryErrorCode);
	}

	handleProductGroupNotFound(): boolean {
		return this.handle('product-group-not-found');
	}

	handleCalculationRequiresReferenceSettings(): boolean {
		return this.handle(ErrorCode.RequiresReferenceSettingsErrorCode);
	}

	handleCalculationRequiredProductSettingsIsMissing(): boolean {
		return this.handle(ErrorCode.RequiredProductSettingsMissingErrorCode);
	}

	handleProductGroupLocked(): boolean {
		return this.handle('product-group-locked');
	}

	handleCannotChangeSettingsForNonMemberProducts(): boolean {
		return this.handle('czech-max-price-calculation-cannot-change-settings-for-non-member-products');
	}

	handleMissingThreeReferenceCountryProductsErrorCode(): boolean {
		return this.handle(ErrorCode.MissingThreeReferenceCountryProductsErrorCode);
	}

	handleIllegalPriceTypeSelected(): boolean {
		return this.handle(ErrorCode.IllegalPriceTypeSelectedErrorCode);
	}

	toValidationResult(): ValidationResult {
		const errors = this.errors.map(mapError);

		return new ValidationResult(
			errors.find((error) => error.code === ErrorCode.RequiresReferenceSettingsErrorCode) !== undefined,
			errors.filter((error) => error.productId !== null),
			errors.filter((error) => error.duplicateCountries.length > 0)
		);
	}
}

export class ProductComputedPrice {
	type: string;
	price: Price;
	description: string;
	selected: boolean;

	constructor(type: string, price: Price, description: string, selected: boolean) {
		this.type = type;
		this.price = price;
		this.description = description;
		this.selected = selected;
	}
}

export class ReferencePrice {
	sourcePrice: ProductPrice;
	calculatedPrice: ProductComputedPrice;

	constructor(sourcePrice: ProductPrice, calculatedPrice: ProductComputedPrice) {
		this.sourcePrice = sourcePrice;
		this.calculatedPrice = calculatedPrice;
	}
}

export class ProductDataSource {
	type: string;
	suklReference: boolean;

	constructor(type: string, suklReference: boolean) {
		this.type = type;
		this.suklReference = suklReference;
	}
}

export class Product {
	product: GeneralProduct;
	datasource: ProductDataSource;
	referencePrices: ReferencePrice[];
	packageSizeForCalculation: number | null;
	packageSizeForSingleUnit: number | null;
	singleUnitPackageSize: boolean;
	selected: boolean;
	productCompliance: ProductCompliance;
	isReferenceCountry: boolean;

	constructor(
		product: GeneralProduct,
		datasource: ProductDataSource,
		referencePrices: ReferencePrice[],
		packageSizeForCalculation: number | null,
		packageSizeForSingleUnit: number | null,
		singleUnitPackageSize: boolean,
		selected: boolean,
		productCompliance: ProductCompliance,
		isReferenceCountry: boolean
	) {
		this.product = product;
		this.datasource = datasource;
		this.referencePrices = referencePrices;
		this.packageSizeForCalculation = packageSizeForCalculation;
		this.packageSizeForSingleUnit = packageSizeForSingleUnit;
		this.singleUnitPackageSize = singleUnitPackageSize;
		this.selected = selected;
		this.productCompliance = productCompliance;
		this.isReferenceCountry = isReferenceCountry;
	}

	findSelectedReferencePrice(): ReferencePrice | null {
		return this.referencePrices.find((price) => price.calculatedPrice.selected) ?? null;
	}
}

export class ProductUpdate {
	id: string;
	priceTypeForCalculation: string | null;
	packageSizeForCalculation: number | null;
	packageSizeForSingleUnit: number | null;
	selected: boolean;

	constructor(
		id: string,
		priceTypeForCalculation: string | null,
		packageSizeForCalculation: number | null,
		packageSizeForSingleUnit: number | null,
		selected: boolean
	) {
		this.id = id;
		this.priceTypeForCalculation = priceTypeForCalculation;
		this.packageSizeForCalculation = packageSizeForCalculation;
		this.packageSizeForSingleUnit = packageSizeForSingleUnit;
		this.selected = selected;
	}
}

export interface FinalCalculationProduct {
	id: string;
	dataSource: DataSource;
}

export class FinalCalculation {
	currency: string | null;
	secondAndThirdLowestPriceAvg: Price | null;
	threeLowestPricesAvg: Price | null;
	deviation: number | null;
	emptyResultReason: Error[];
	fromProducts: FinalCalculationProduct[];

	constructor(
		currency: string | null,
		secondAndThirdLowestPriceAvg: Price | null,
		threeLowestPricesAvg: Price | null,
		deviation: number | null,
		emptyResultReason: Error[],
		fromProducts: FinalCalculationProduct[]
	) {
		this.currency = currency;
		this.secondAndThirdLowestPriceAvg = secondAndThirdLowestPriceAvg;
		this.threeLowestPricesAvg = threeLowestPricesAvg;
		this.deviation = deviation;
		this.emptyResultReason = emptyResultReason;
		this.fromProducts = fromProducts;
	}

	hasError(code: ErrorCode): boolean {
		return this.emptyResultReason.some((reason) => reason.code === code);
	}

	findOnlyOneProductPerCountryError(): Error | null {
		return this.emptyResultReason.find((reason) => reason.code === ErrorCode.OnlyOneProductPerCountryErrorCode) ?? null;
	}
}

export class Reference {
	packageSize: number | null;
	referenceBasketVersion: string;

	constructor(packageSize: number | null, referenceBasketVersion: string) {
		this.packageSize = packageSize;
		this.referenceBasketVersion = referenceBasketVersion;
	}
}

export class CzMaxPrice {
	reference: Reference;
	products: Product[];
	finalCalculation: FinalCalculation;
	links: { [key: string]: string };

	constructor(
		reference: Reference,
		products: Product[],
		finalCalculation: FinalCalculation,
		links: { [key: string]: string }
	) {
		this.reference = reference;
		this.products = products;
		this.finalCalculation = finalCalculation;
		this.links = links;
	}

	getExportXlsLink(): string {
		return this.links['export-xls'];
	}

	selectExFactoryPrice(): Price | null {
		for (const product of this.products) {
			if (product.product.dataset.source.country.alpha2 !== 'CZ') continue;
			const price = product.product.sourcePrices.find((p) => p.type === 'ex-factory');
			if (price !== undefined) return price.price;
		}
		return null;
	}
}
